import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* --------------------------------------------------------------
   Config — Esri World Imagery (satellite) rotated 90° (north→left);
             random military‑vehicle icons from /img/maps;
             all pan/zoom interactions disabled
-------------------------------------------------------------- */
const CENTER = [44.287683, -93.971883];
const VEHICLE_COUNT = 7;
const UPDATE_MS = 250;
const STEP_DEG = 0.0002; // ≈ 45 m per hop (larger range)
const START_RADIUS = 0.002; // ≈ 550 m initial scatter

/* Available icons inside public/img/maps */
const ICON_PATHS = [
  "/img/maps/abbott__1_-removebg-preview.png",
  "/img/maps/apc-removebg-preview.png",
  "/img/maps/apc-removebg-preview (1).png",
  "/img/maps/IFV-removebg-preview.png",
  "/img/maps/humvee_green-removebg-preview.png",
  "/img/maps/t55-removebg-preview.png",
];

function createRandomIcon() {
  const src = ICON_PATHS[Math.floor(Math.random() * ICON_PATHS.length)];
  return L.icon({
    iconUrl: src,
    iconSize: [80, 80],
    iconAnchor: [40, 40],
    popupAnchor: [0, -40],
  });
}

export default function VehicleMap() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    /* 1. Leaflet map — interactions off */
    mapRef.current = L.map(containerRef.current, {
      zoomControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      tap: false,
      touchZoom: false,
    }).setView(CENTER, 17);

    /* 1a. Satellite layer — Esri World Imagery */
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        attribution:
          "Imagery © Esri, Maxar, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN, and the GIS User Community",
        maxZoom: 19,
      }
    ).addTo(mapRef.current);

    /* 2. Spawn vehicles with random icon */
    for (let i = 1; i <= VEHICLE_COUNT; i++) {
      const start = randomOffset(CENTER, START_RADIUS);
      const icon = createRandomIcon();
      const marker = L.marker(start, { icon })
        .addTo(mapRef.current)
        .bindPopup(`Vehicle ${i}`);
      markersRef.current[i] = marker;
      rotateIconUpright(marker._icon);
    }

    /* 3. Animation loop */
    const interval = setInterval(() => {
      Object.entries(markersRef.current).forEach(([id, marker]) => {
        const { lat, lng } = marker.getLatLng();
        const [nLat, nLng] = randomOffset([lat, lng], STEP_DEG);

        marker.setLatLng([nLat, nLng]);
        if (marker._icon) {
          marker._icon.style.transition = "transform 0.25s linear";
          rotateIconUpright(marker._icon);
        }

        if (marker.isPopupOpen()) {
          marker.setPopupContent(
            `Vehicle ${id}<br/>${nLat.toFixed(5)}, ${nLng.toFixed(5)}`
          );
        }
      });
    }, UPDATE_MS);

    return () => {
      clearInterval(interval);
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  /* Wrapper with rotation */
  const wrapperStyle = {
    width: "100vw",
    height: "100vh",
    overflow: "hidden",
  };
  const rotatedStyle = {
    width: "100vh",
    height: "100vw",
    transform: "rotate(-90deg) translate(-100%, 0)",
    transformOrigin: "top left",
  };

  return (
    <div style={wrapperStyle}>
      <div ref={containerRef} style={rotatedStyle} />
    </div>
  );
}

/* --------------------------------------------------------------
   Helpers
-------------------------------------------------------------- */
function randomOffset([lat, lng], radius) {
  const dLat = (Math.random() * 2 - 1) * radius;
  const dLng = (Math.random() * 2 - 1) * radius;
  return [lat + dLat, lng + dLng];
}

function rotateIconUpright(iconEl) {
  if (!iconEl) return;
  const base = iconEl.style.transform || "";
  const cleaned = base.replace(/rotate\([^)]*\)/, "").trim();
  iconEl.style.transform = `${cleaned} rotate(90deg)`;
}
